<!--
  `#/` — the landing page, and the shell the later phases will grow into.

  It used to be one scroll: five honesty flags, the whole KPI dashboard, the orientation prose,
  a keyboard table and five Konva examples. Everything on it was there for a reason and none of
  it could be found. So it is a **vertical tab rail** over the section table in
  `lib/homeTabs.ts`, with **Getting started** first and default *(changed 2026-08-09 — it was the
  dashboard, and landing a newcomer on six tables of zeros is what "overwhelming" meant)*. The
  numbers are one arrow key away, which is where the orientation used to be.

  Three things are deliberate:

  * **The tab is in the URL** (`#/home/keyboard`), not in component state. A reload keeps you
    where you were, a colleague can be sent straight to the shortcut table, and an unknown id
    falls back to the default tab rather than to a blank panel.
  * **The intro stays outside the rail.** What the transliteration is — a weak label, a
    scholar's reading of a weathered rock, not ground truth — qualifies everything on every
    panel, so it is not something you can be on the wrong tab to read.
  * **Real tab semantics.** `role="tablist"` / `role="tab"` / `role="tabpanel"`, roving
    `tabindex`, arrows to move, Home/End to jump — and the tabs are anchors, so middle-click
    opens a copy and "copy link address" gives something that works. Focus is never trapped.

  Adding a section in a later phase is an entry in `HOME_TABS` plus its panel in `PANELS` below;
  the record is keyed by the tab id, so an entry with no panel fails `npm run check` rather than
  rendering an empty tab. There are deliberately **no placeholder tabs** for phases that do not
  exist yet.
-->
<script lang="ts">
  import { untrack, type Component } from 'svelte';

  import { HOME_TABS, type HomeTabId } from '../lib/homeTabs';
  import { href, router } from '../lib/router.svelte';
  import { queue } from '../lib/stores/queue.svelte';
  import HomeAlphabet from './HomeAlphabet.svelte';
  import HomeConventions from './HomeConventions.svelte';
  import HomeGettingStarted from './HomeGettingStarted.svelte';
  import HomeKeyboard from './HomeKeyboard.svelte';
  import StatsDashboard from './StatsDashboard.svelte';

  /**
   * The panel behind each tab id. Typed as a total record on purpose: adding an id to
   * `HOME_TABS` without a panel here is a type error, which is the only way "one entry" stays
   * honest as this page grows.
   */
  const PANELS: Record<HomeTabId, Component> = {
    dashboard: StatsDashboard,
    start: HomeGettingStarted,
    conventions: HomeConventions,
    alphabet: HomeAlphabet,
    keyboard: HomeKeyboard,
  };

  const TABS = HOME_TABS.map((meta) => ({ ...meta, component: PANELS[meta.id] }));

  // Cards drive the call to action above. `ensureCards` is a no-op once they are in hand, so
  // this costs one request per session rather than one per visit to the landing page.
  //
  // `untrack`, for the reason every other store call in this app is untracked: `loadCards`
  // writes `cards`, `cardsLoading` and `cardsProblem`, and the guard inside `ensureCards`
  // *reads* the first two. Tracked, the effect depends on the fields its own call assigns and
  // re-runs forever — and it does not fail politely, it wedges the tab, which is how it was
  // found (`HomePage.dom.test.ts` stopped terminating rather than stopped passing).
  $effect(() => {
    untrack(() => void queue.ensureCards());
  });

  /**
   * The task the intro button offers.
   *
   * The first card with work left and nothing blocking it. Deliberately not "the task with the
   * most left" or any other ranking: the chooser exists for choosing, and a landing page that
   * silently reorders the work would be making a judgement it has no basis for. Null until the
   * cards arrive, which is the generic-wording case.
   */
  const headlineTask = $derived(
    queue.cards?.find((card) => !card.blocked_reason && card.progress.remaining_images > 0) ??
      null,
  );

  // `parse` already resolved an unknown tab to the default, so this only has to cope with
  // being rendered from some other route (it is not) — the fallback is belt and braces.
  const activeId = $derived(router.route.name === 'home' ? router.route.tab : TABS[0]!.id);
  const active = $derived(TABS.find((tab) => tab.id === activeId) ?? TABS[0]!);

  /**
   * Below this width the rail becomes a horizontal strip that scrolls sideways, because a
   * 220px column beside a wide stats table leaves the table unreadable on a laptop in portrait
   * or a split screen. `aria-orientation` follows the layout rather than staying vertical: a
   * screen-reader user being told "vertical" while the arrow keys they are offered run along a
   * row is worse than no orientation at all. Both axes are accepted either way, so nothing
   * depends on getting the query right.
   */
  let narrow = $state(false);
  $effect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(max-width: 760px)');
    const sync = () => {
      narrow = query.matches;
    };
    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  });

  function select(id: HomeTabId, rail: HTMLElement | null): void {
    router.go({ name: 'home', tab: id });
    // The anchors are keyed by id and never recreated, so the new tab can be focused straight
    // away; `tabindex` catches up on the next flush and programmatic focus does not care.
    rail?.querySelector<HTMLElement>(`[data-tab="${id}"]`)?.focus();
  }

  /**
   * Roving arrow-key navigation, wrapping, with Home/End.
   *
   * Both axes are live: the rail is vertical on a wide screen and horizontal on a narrow one,
   * and a tab rail that ignores the arrow the user reached for is indistinguishable from a
   * broken one. The handler sits on the tab itself rather than on the rail, because the tab is
   * what holds focus — the rail is never a tab stop, which is the whole point of a roving
   * `tabindex`.
   */
  function onTabKeydown(event: KeyboardEvent): void {
    const anchor = event.currentTarget as HTMLElement;
    const rail = anchor.closest<HTMLElement>('[role="tablist"]');
    const index = TABS.findIndex((tab) => tab.id === anchor.dataset.tab);
    let next = index;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        next = (index + 1) % TABS.length;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        next = (index - 1 + TABS.length) % TABS.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = TABS.length - 1;
        break;
      default:
        return;
    }
    // Only after a key we own: Tab, Enter and everything else keep their browser behaviour, and
    // focus is never trapped in the rail.
    event.preventDefault();
    select(TABS[next]!.id, rail);
  }
</script>

<div class="home col">
  <header class="panel col intro">
    <h1>Machine-assisted reading of Hismaic inscriptions</h1>
    <p class="small">
      This tool turns OCIANA's per-inscription transliterations into boxed letters. The
      transliteration tells us <em>which</em> letters are there and <em>in what order</em>; it
      does not tell us <em>where</em>, and it is a scholar's reading of a weathered rock rather
      than ground truth. Your boxes supply the geometry, and your disagreements with the reading
      are recorded as disagreements rather than reconciled away.
    </p>
    <!--
      The call to action names a job, not a screen.

      "Go to the work queue" is a destination; somebody who does not yet know what the queue is
      cannot tell whether it is where they should be going. Once the cards have loaded there is
      a concrete answer available — a task, and how much of it is left — so the button says
      that instead, and the generic wording is only the pre-load fallback.
    -->
    <div class="row wrap actions">
      {#if headlineTask}
        <a class="button-link" href={href({ name: 'queue', task: headlineTask.id })}>
          {headlineTask.verb} — {headlineTask.progress.remaining_images} image(s) left
        </a>
        <a class="button-link subtle-link" href={href({ name: 'queue', task: null })}>
          All tasks
        </a>
      {:else}
        <a class="button-link" href={href({ name: 'queue', task: null })}>Go to the work queue</a>
      {/if}
      <span class="spacer"></span>
      <!-- A link, not a path *(2026-08-09)*. This printed `{GUIDELINES}` as mono text, which
           is only useful to somebody with a checkout — and the people this is for have a
           browser. `#/guidelines` serves the same file. -->
      <a class="small" href={href({ name: 'guidelines', at: null })}>Full conventions</a>
    </div>
  </header>

  <div class="tabs" class:narrow>
    <div
      class="rail"
      role="tablist"
      aria-label="Home sections"
      aria-orientation={narrow ? 'horizontal' : 'vertical'}
    >
      {#each TABS as tab (tab.id)}
        <a
          class="tab"
          class:current={tab.id === active.id}
          role="tab"
          id="home-tab-{tab.id}"
          data-tab={tab.id}
          href={href({ name: 'home', tab: tab.id })}
          aria-selected={tab.id === active.id}
          aria-controls="home-panel-{tab.id}"
          tabindex={tab.id === active.id ? 0 : -1}
          onkeydown={onTabKeydown}
        >
          <span class="label">{tab.label}</span>
          <span class="small muted desc">{tab.description}</span>
        </a>
      {/each}
    </div>

    <div
      class="panel-host"
      role="tabpanel"
      id="home-panel-{active.id}"
      data-panel={active.id}
      aria-labelledby="home-tab-{active.id}"
      tabindex="0"
    >
      {#key active.id}
        {@const Panel = active.component}
        <Panel />
      {/key}
    </div>
  </div>
</div>

<style>
  .home {
    padding: 12px;
    gap: 12px;
    max-width: 1280px;
    margin: 0 auto;
    width: 100%;
  }

  .intro p {
    max-width: 82ch;
    margin: 0;
  }

  .actions {
    gap: 10px;
  }

  .button-link {
    display: inline-block;
    background: var(--accent-dim);
    border: 1px solid var(--accent);
    border-radius: 5px;
    padding: 5px 10px;
    color: var(--text);
    text-decoration: none;
  }

  .button-link.subtle-link {
    background: var(--bg-raised);
    border-color: var(--line-strong);
  }

  .button-link:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }

  .tabs {
    display: grid;
    grid-template-columns: 232px minmax(0, 1fr);
    gap: 12px;
    align-items: start;
  }

  .rail {
    display: flex;
    flex-direction: column;
    gap: 4px;
    position: sticky;
    top: 12px;
  }

  .tab {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 7px 10px;
    border: 1px solid var(--line);
    /* The selected tab is marked by a heavy left edge and a raised background as well as by
       colour — the same rule the box styles follow: colour is never the only signal. */
    border-left: 4px solid transparent;
    border-radius: 5px;
    background: var(--bg-panel);
    color: var(--text-dim);
    text-decoration: none;
  }

  .tab:hover {
    background: var(--bg-raised);
  }

  .tab.current {
    background: var(--bg-raised);
    border-color: var(--line-strong);
    border-left-color: var(--accent);
    color: var(--text);
  }

  .tab:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }

  .tab .label {
    font-weight: 600;
  }

  .panel-host {
    min-width: 0;
  }

  .panel-host:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }

  /* Narrow: a horizontal strip that scrolls sideways rather than a column that eats the width.
     The descriptions go — they are the first thing worth losing, and the labels carry the
     meaning. */
  .tabs.narrow {
    grid-template-columns: minmax(0, 1fr);
  }

  .tabs.narrow .rail {
    flex-direction: row;
    overflow-x: auto;
    position: static;
    padding-bottom: 2px;
  }

  .tabs.narrow .tab {
    flex: none;
    border-left: 1px solid var(--line);
    border-bottom: 4px solid transparent;
  }

  .tabs.narrow .tab.current {
    border-color: var(--line-strong);
    border-bottom-color: var(--accent);
  }

  .tabs.narrow .desc {
    display: none;
  }
</style>
